import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Creator, AnalyticsData } from '../types';
import { BackArrowIcon, UpArrowIcon } from '../components/Icons';
import { TotalViewsChart, EngagementChart } from '../components/Charts';

interface AnalyticsProps {
    creators: Creator[];
    analyticsData: AnalyticsData;
}

const Analytics: React.FC<AnalyticsProps> = ({ creators, analyticsData }) => {
    const { creatorId } = useParams<{ creatorId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [timeRange, setTimeRange] = useState<'30D' | '6M' | '1Y'>('6M');

    const creator = creators.find(c => c.id === creatorId);

    const translatedEngagementData = useMemo(() => 
        analyticsData.engagement.map(d => ({...d, name: t(`platform.${d.name}`)})), 
        [analyticsData.engagement, t]
    );

    const translatedPerformanceData = useMemo(() =>
        analyticsData.performance.map(d => ({...d, name: t(`platform.${d.name}`)})),
        [analyticsData.performance, t]
    );

    if (!creator) {
        return <div className="p-4 text-center text-gray-600 dark:text-gray-300">{t('analytics.creatorNotFound')}</div>;
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
            <header className="p-4 sm:p-6 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
                 <div className="flex items-center gap-4">
                    <button onClick={() => navigate(-1)} className="flex size-8 shrink-0 items-center justify-center text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
                        <BackArrowIcon />
                    </button>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('analytics.title')} for {creator.name}</h1>
                 </div>
                <div className="flex items-center gap-1 rounded-lg bg-gray-100 dark:bg-gray-700 p-1">
                    {(['30D', '6M', '1Y'] as const).map(range => (
                        <button 
                            key={range}
                            onClick={() => setTimeRange(range)}
                            className={`rounded-md px-3 py-1 text-sm font-medium ${timeRange === range ? 'bg-white dark:bg-gray-800 text-[var(--primary-color)] shadow-sm' : 'text-gray-700 dark:text-gray-300'}`}
                        >
                            {range}
                        </button>
                    ))}
                </div>
            </header>
            
            <main className="p-4 sm:p-6">
                 <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">{t('analytics.performanceOverview')}</h2>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
                        <p className="mb-1 text-base font-medium text-gray-600 dark:text-gray-400">{t('analytics.totalViews')}</p>
                        <div className="flex items-baseline gap-2">
                            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">2.1M</p>
                            <div className="flex items-center gap-1 text-sm font-medium text-green-600">
                                <UpArrowIcon />
                                <span>+15%</span>
                            </div>
                        </div>
                        <TotalViewsChart data={analyticsData.views} />
                    </div>

                    <div className="lg:col-span-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
                        <p className="mb-1 text-base font-medium text-gray-600 dark:text-gray-400">{t('analytics.engagementByPlatform')}</p>
                        <div className="flex items-baseline gap-2">
                             <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">8.2%</p>
                            <div className="flex items-center gap-1 text-sm font-medium text-green-600">
                                <UpArrowIcon />
                                <span>+8%</span>
                            </div>
                        </div>
                        <EngagementChart data={translatedEngagementData} />
                    </div>
                    
                    <div className="lg:col-span-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
                        <p className="mb-1 text-base font-medium text-gray-600 dark:text-gray-400">{t('analytics.contentPerformance')}</p>
                         <div className="flex items-baseline gap-2">
                             <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('analytics.avgLikes', { amount: '12K' })}</p>
                             <div className="flex items-center gap-1 text-sm font-medium text-green-600">
                                 <UpArrowIcon />
                                 <span>+12%</span>
                             </div>
                         </div>
                        <div className="mt-4 space-y-4">
                            {translatedPerformanceData.map(item => (
                                <div key={item.name} className="grid grid-cols-[120px_1fr_auto] items-center gap-4">
                                    <p className="truncate text-sm font-medium text-gray-600 dark:text-gray-400">{item.name}</p>
                                    <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                                        <div className="h-2 rounded-full bg-[var(--primary-color)]" style={{ width: `${item.performance}%` }}></div>
                                    </div>
                                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{item.performance}%</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Analytics;